import axios from "axios";
import * as Sentry from "@sentry/react";
import { addToErrorMessage } from "../features/errorMessage/errorMessageSlice";
import { addToLoader } from "../features/loader/loaderSlice";
import { getInstallId } from "../utilities/installId";

const apiResources = {
    apiKey: import.meta.env.VITE_API_KEY,
    secretKey: import.meta.env.VITE_SECRET_KEY,
    baseUrl: import.meta.env.VITE_BASE_URL,
    endPoint: "app_endpoints.php",
    imageUploadPass: import.meta.env.VITE_IMAGE_UPLOAD_PASS,
    endPointUploadImage: "upload_image.php",
};

export const getUserDetails = () => {
    const raw = localStorage.getItem("appUser");
    if (!raw || raw === "undefined" || raw === "null") return false;

    // A corrupt or legacy-schema `appUser` used to throw here (or later, when a
    // caller dereferenced user_session/user_details) on every visit until the
    // user cleared storage — a recurring whitescreen. Treat anything malformed
    // as logged out and remove it so the next visit starts clean.
    let userDetails;
    try {
        userDetails = JSON.parse(raw);
    } catch {
        destroyUserDetails();
        return false;
    }

    if (!userDetails?.user_details || !userDetails?.user_session?.user_session_token) {
        destroyUserDetails();
        return false;
    }

    return userDetails;
};

// Attach the logged-in user to every Sentry event. Without this the
// invalid-session warnings emitted by the forced-logout handler below are
// anonymous, so a cluster of them cannot be told apart from a single machine
// retrying — which is exactly the signal needed to judge whether sessions are
// being clobbered across the fleet. Only the internal uid is attached, which is
// less identifying than what `sendDefaultPii: true` already sends.
//
// Telemetry only: nothing in the app reads this back.
export const syncSentryUser = () => {
    const userDetails = getUserDetails();
    Sentry.setUser(userDetails ? { id: userDetails.user_details.uid } : null);
};

export const setUserDetails = (param) => {
    // Mirror getUserDetails' invariant: never store a payload that would be
    // destroyed on the next read. The backend login can return
    // `"user_session": false` inside an otherwise successful response
    // (duplicate session rows for the same user+device — Auth::getUserSession
    // requires exactly one row); storing it used to make the login "succeed"
    // and strand the user with a token-less session that fails every
    // subsequent request. Returning false here surfaces the existing
    // "Login failed" message at the call sites instead.
    if (!param?.user_details || !param?.user_session?.user_session_token) {
        return false;
    }
    localStorage.setItem("appUser", JSON.stringify(param));
    Sentry.setUser({ id: param.user_details.uid });
    return true;
};

export const destroyUserDetails = () => {
    localStorage.removeItem("appUser");
    // Clear before the forced-logout redirect, so events raised on the way back
    // to the login screen are not attributed to the user who was just kicked.
    Sentry.setUser(null);
};

const SESSION_EXPIRED_KEY = "mrgSessionExpired";

// A forced logout used to drop the user at a bare login screen with no
// explanation — they were mid-task and the app appeared to restart, which is
// why the WebView whitescreen was first reported as a crash.
//
// The copy is resolved here rather than in the login screens because only
// LoginNew has any translation wiring: CMSLogin, RoyalBridgeLogin and
// RoyalMomentumLogin are English-only today, so a per-screen notice would mean
// retrofitting i18n into three components for one sentence. Instead the message
// goes through the global <ErrorMessage> popup already mounted in App and
// AppRoyalMomentum. Language detection mirrors AppErrorFallback.
const sessionExpiredText = () => {
    let isSpanish = false;
    try {
        isSpanish = (window.currentLanguage || localStorage.getItem("mrgCurrentLanguage")) === "es";
    } catch {
        isSpanish = false;
    }
    return isSpanish
        ? "Tu sesión ha expirado. Vuelve a iniciar sesión."
        : "Your session expired. Please log in again.";
};

// Best-effort dispatch through the store. Lazy import — a top-level `store`
// import would be a module cycle (store → slices → apiRequest).
const dispatchLazily = (action) =>
    import("../app/store")
        .then(({ store }) => store.dispatch(action))
        .catch(() => {
            /* best-effort — never let a notice block the logout */
        });

// Consumed once per document load by both entries. Only the http(s) redirect
// below needs this: it is a real document load, so Redux is gone by the time the
// login screen renders and the notice has to survive in sessionStorage. The
// WebView path keeps its store and dispatches directly instead.
export const showSessionExpiredNoticeIfPending = () => {
    let pending = false;
    try {
        pending = !!sessionStorage.getItem(SESSION_EXPIRED_KEY);
        // Clear on read, or every later reload in this tab re-shows the notice.
        if (pending) sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    } catch {
        return;
    }
    if (pending) dispatchLazily(addToErrorMessage(sessionExpiredText()));
};

// Which login screen belongs to the route the user was on. The forced logout
// used to always target "/" — the My Royal Genie login — so a Royal Bridge user
// or CMS admin logged out on the web landed on a login that is not theirs, and
// an `is_mrg_user: false` account then hits the access-denied gate.
//
// CMS is deliberately English-only here: it has no `/cms/es` route, and
// handlePathsBasedOnLanguage appends the stored language suffix to whatever we
// land on, so a Spanish admin sent to "/cms" would be pushed to "/cms/es" and
// match the catch-all ErrorPage. "/" is the wrong platform but it works, which
// is the better failure. Drop the flag once /cms/es exists.
const LOGIN_ROUTES = [
    { prefix: "/royal-bridge", login: "/royal-bridge/login" },
    { prefix: "/royal-momentum", login: "/royal-momentum/login" },
    // Royal Momentum reset password is a sibling of "/royal-momentum", not a child of
    // it, so the segment-exact test below needs it spelled out separately.
    { prefix: "/royal-momentum-reset-password", login: "/royal-momentum/login" },
    { prefix: "/cms", login: "/cms", englishOnly: true },
];

// `route` is the in-app route, which is NOT always location.pathname: the WebView
// builds use hash routing, where pathname is the filesystem path to index.html
// and the route lives in location.hash. Callers pass the right one — getting this
// wrong is how "/" ended up navigating a WebView to the filesystem root.
export const loginPathForRoute = (route) => {
    const path = String(route || "/").split(/[?#]/)[0].toLowerCase();
    const match = LOGIN_ROUTES.find(
        (candidate) => path === candidate.prefix || path.startsWith(`${candidate.prefix}/`)
    );
    if (!match) return "/";
    if (match.englishOnly) {
        let language = null;
        try {
            language = localStorage.getItem("mrgCurrentLanguage");
        } catch {
            language = null;
        }
        if (language && language !== "en") return "/";
    }
    return match.login;
};

// Full-document return to the login screen. On http(s) the app root is "/",
// but the WebView builds (Windows / mobile Royal Pro) load the document from
// the local filesystem with hash routing — there `href = "/"` navigates the
// WebView to the filesystem root: a silent white page that throws nothing, so
// Sentry never sees it.
//
// No reload() on non-http schemes either: a container that injects the HTML
// (NavigateToString or similar) cannot re-fetch the document by URL, so a
// reload resolves to a blank page — the same whitescreen by another route.
// The hash change alone routes the now-logged-out app to the login screen.
export const redirectToLoginScreen = () => {
    // Detect the WebView builds by their hash routing, not by URL scheme: the
    // production container serves the bundle from an https virtual host
    // (https://vizcon.local), so scheme sniffing sends WebView logouts through
    // the pathname branch — a navigation the virtual host cannot serve. A
    // hash-routed document always carries "#/..."; the BrowserRouter web apps
    // never do.
    if (!window.location.hash.startsWith("#/")) {
        // Redux does not survive the document load, so the notice is handed over
        // in sessionStorage and picked up by the entry on the way back up.
        try {
            sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
        } catch {
            /* notice is best-effort — never let it block the logout */
        }
        // Browser routing: the route is the pathname.
        window.location.href = loginPathForRoute(window.location.pathname);
        return;
    }
    // Without a document reload the Redux store survives the logout. Clear the
    // global loader: a caller that showed it and awaits the never-settling
    // promise from the invalid-session handler would otherwise leave the
    // full-screen overlay covering the login forever.
    dispatchLazily(addToLoader(false));
    // Store intact and no reload coming, so show the notice directly rather than
    // routing it through sessionStorage.
    dispatchLazily(addToErrorMessage(sessionExpiredText()));
    // Hash routing: the route is the hash, never the pathname. Assigning a
    // leading-slash path here keeps the navigation inside the document.
    window.location.hash = loginPathForRoute(window.location.hash.replace(/^#/, ""));
};

// Sessions are keyed per (user, device_app_id) on the backend, and the raw
// user agent is identical across identically-patched fleet machines — so those
// machines all shared ONE session row, and LOGOUT_USER (which deletes by
// user+device) on one machine killed the token every other machine still held
// (the "shared-UA token clobber"). A persisted per-install suffix gives each
// install its own session row. The backend treats device_app_id as an opaque
// string, so no backend change is needed; sessions created under the old
// UA-only value keep validating via the backend's token-only fallback (MR !44).
//
// DEPLOYMENT GATE: ship only after backend MR !46 (token-exact session checks
// + its migration) is live in production — pre-!46 the token-blind boot check
// looks up a row by the NEW device id, finds none, and force-logs-out every
// existing session at boot.
// The install id itself lives in utilities/installId.js — the GPU-reset
// watchdog needs the same value for telemetry attribution.
const getDeviceAppId = () => {
    const installId = getInstallId();
    // Storage unavailable — fall back to the legacy UA-only identifier.
    if (!installId) return navigator.userAgent;
    return `${navigator.userAgent}::${installId}`;
};

// Only a no-response failure (connection dropped, not a timeout or HTTP error) on a read is
// retried: the server may have processed a dropped write, so replaying one could duplicate it.
const isRetryableNetworkError = (type, error) =>
    error?.code === "ERR_NETWORK" &&
    !error.response &&
    (String(type).startsWith("GET_") || type === "CHECK_USER_SESSION");

export const apiRequest = async ({
    method = "POST",
    requestType,
    payload = {},
    onUploadProgress = null,
    responseType = "json",
}) => {
    const url = apiResources.baseUrl + apiResources.endPoint;
    const userDetails = getUserDetails();
    const defaultHeaders = {
        "Content-Type": "application/json",
    };

    // Signs and sends once. Re-run on retry so every attempt carries a fresh
    // timestamp + nonce — the backend rejects a repeated access-token hash.
    const send = async () => {
        let timestamp = new Date().getTime();
        let nonce = generateNonce(20);

        if (payload instanceof FormData) {
            defaultHeaders["Content-Type"] = "multipart/form-data";
            let dataElement = JSON.parse(payload.get("data"));
            dataElement.device_app_id = getDeviceAppId();
            dataElement.request_type = requestType;
            dataElement.timestamp = timestamp;
            dataElement.api_key = apiResources.apiKey;
            dataElement.nonce = nonce;
            if (userDetails) {
                dataElement.user_session_token = userDetails.user_session.user_session_token;
            }
            const clientApiAccessToken = await generateApiAccessToken(
                "SHA-256",
                apiResources.apiKey +
                    apiResources.secretKey +
                    dataElement.timestamp +
                    dataElement.nonce +
                    (userDetails ? dataElement.user_session_token : "")
            );
            dataElement.api_access_token = clientApiAccessToken;
            payload.set("data", JSON.stringify(dataElement));
        } else {
            payload.device_app_id = getDeviceAppId();
            payload.request_type = requestType;
            payload.timestamp = timestamp;
            payload.api_key = apiResources.apiKey;
            payload.nonce = nonce;
            if (userDetails) {
                payload.user_session_token = userDetails.user_session.user_session_token;
            }
            const clientApiAccessToken = await generateApiAccessToken(
                "SHA-256",
                apiResources.apiKey +
                    apiResources.secretKey +
                    payload.timestamp +
                    payload.nonce +
                    (userDetails ? payload.user_session_token : "")
            );
            payload.api_access_token = clientApiAccessToken;
        }

        return axios({
            method,
            url,
            data: payload,
            headers: defaultHeaders,
            onUploadProgress: onUploadProgress,
            responseType,
            // A hung request must eventually reject: callers gate loaders/first-load
            // state on this promise settling, and a request that never settles left
            // the full-screen loader up forever with nothing for Sentry to report.
            // FormData gets longer for large campaign-image uploads.
            timeout: payload instanceof FormData ? 120000 : 30000,
        });
    };

    let response;
    try {
        response = await send();
    } catch (error) {
        if (!isRetryableNetworkError(requestType, error)) throw error;
        // rcideloreanbe.2viz.io intermittently drops connections (Sentry FRONTEND-AY/B7/78/D4/D2);
        // a second attempt usually lands on a healthy connection.
        response = await send();
    }

    // The backend distinguishes three auth-mechanism failures: a timestamp
    // outside the 15-minute window (ERROR_API_CALL_EXPIRED), a duplicate
    // access-token hash (ERROR_API_CALL_INVALID_API_ACCESS_TOKEN), and a dead
    // session token (ERROR_INVALID_USER_SESSION, handled below). Logging the
    // first two lets Sentry discriminate which mechanism a forced-logout
    // cluster actually is; not gated on userDetails — clock skew also hits
    // session-less public requests.
    if (
        response?.data?.status === 1 &&
        ["ERROR_API_CALL_EXPIRED", "ERROR_API_CALL_INVALID_API_ACCESS_TOKEN"].includes(
            response.data.error_code
        )
    ) {
        Sentry.captureMessage(`${response.data.error_code} on ${requestType}`, "warning");
    }

    if (userDetails && response.data.status === 1) {
        const errorCode = response.data.error_code;
        // Two codes end the session for good. ERROR_INVALID_USER_SESSION is a
        // dead token. ERROR_USER_NOT_EXISTS is the backend no longer resolving
        // the uid behind the stored session to a user (deleted, or provisioned
        // on another environment's database). Left unhandled, the second kept
        // the authenticated shell alive with every data call rejected: empty
        // screens, the 10-minute notification poll still running, one Sentry
        // event per rejection — on a kiosk indistinguishable from a whitescreen
        // (container handover, 2026-09-03). It is only terminal when the
        // request was about the session user: a request carrying someone
        // else's uid (CMS user management, password reset) getting this code
        // says nothing about our own session.
        const isTerminalSessionError =
            errorCode === "ERROR_INVALID_USER_SESSION" ||
            (errorCode === "ERROR_USER_NOT_EXISTS" &&
                isAboutSessionUser(payload, userDetails.user_details?.uid));
        if (isTerminalSessionError) {
            // This forced logout is a deliberate navigation, not an exception —
            // record it so the resulting "app restarted at login" is visible in
            // Sentry instead of being indistinguishable from a crash.
            //
            // Capture BEFORE destroyUserDetails(): that call clears the Sentry
            // user, and captureMessage binds the current scope synchronously, so
            // this ordering is what makes the warning carry the uid — which is
            // the whole point of having it (grouping forced logouts by user is
            // how you tell a fleet-wide session clobber from one machine
            // retrying). Do not reorder these two lines. The invalid-session
            // text is unchanged — existing Sentry issues and alerts key on it.
            Sentry.captureMessage(
                errorCode === "ERROR_INVALID_USER_SESSION"
                    ? `Invalid user session on ${requestType} — logging out and returning to login`
                    : `User no longer exists on ${requestType} — logging out and returning to login`,
                "warning"
            );
            destroyUserDetails();
            redirectToLoginScreen();
            // The page is navigating away; never settle so callers don't race
            // the redirect and dereference `.data` on undefined.
            return new Promise(() => {});
        }
    }

    reportSqlError(requestType, response);

    return response;
};

// True when the request's `user_uid` is the session user's, or when it carries
// none (the backend then resolves the user from the session token). False only
// for a request explicitly about another user.
const isAboutSessionUser = (payload, sessionUid) => {
    try {
        const data = payload instanceof FormData ? JSON.parse(payload.get("data")) : payload?.data;
        const requestUid = data?.user_uid;
        if (requestUid === undefined || requestUid === null || requestUid === "") return true;
        return String(requestUid) === String(sessionUid);
    } catch {
        return true;
    }
};

// ERROR_SQL means the query itself failed on the backend (the real cause is only in the BE error
// log). Individual call sites handle it inconsistently — many only read `data`, so the request
// fails silently and the screen just renders empty. Surfacing it centrally means every endpoint
// gets the popup for free, via the global <ErrorMessage> already mounted in App/AppRoyalMomentum.
//
// Deliberately NOT gated on `userDetails` like the session check above: the public routes (link
// details, external meeting summary, KAM/store-visit public summaries) hit the same failure with
// no session at all.
const reportSqlError = (requestType, response) => {
    // responseType "blob"/"arraybuffer" (file downloads) gives a non-plain body — guard for it.
    if (!response?.data || typeof response.data !== "object") return;
    if (response.data.error_code !== "ERROR_SQL") return;

    // Lead with the request_type: the backend message is a generic "contact administrator" string,
    // so the endpoint name is the only actionable part for whoever sees the popup.
    const text = `SQL error on ${requestType}. ${response.data.error_message ?? ""}`.trim();

    console.error("[ERROR_SQL]", requestType, response.data);

    // `store` is imported lazily on purpose: store.js → feature slices → apiRequest.js is a real
    // import cycle, so a top-level import would leave `store` undefined at module-init time.
    import("../app/store")
        .then(({ store }) => store.dispatch(addToErrorMessage(text)))
        .catch(() => {
            /* popup is best-effort — never let reporting break the original request */
        });
};

const generateApiAccessToken = (algo, str) => {
    return crypto.subtle.digest(algo, new TextEncoder("utf-8").encode(str)).then((buf) => {
        return Array.prototype.map
            .call(new Uint8Array(buf), (x) => ("00" + x.toString(16)).slice(-2))
            .join("");
    });
};

const generateNonce = (length) => {
    // The backend rejects duplicate access-token hashes (same timestamp + nonce
    // + token), so nonce uniqueness matters inside same-millisecond parallel
    // request bursts — use the CSPRNG rather than Math.random.
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const bytes = crypto.getRandomValues(new Uint8Array(length));
    let text = "";
    for (let i = 0; i < length; i++) {
        text += possible.charAt(bytes[i] % possible.length);
    }
    return text;
};
